import React, { useEffect, useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import {
  makeStyles,
  Tabs,
  Tab,
  Typography,
  Box,
  Hidden,
  AppBar,
  Paper,
  Button,
  Container,
  Grid,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Switch,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { Visibility, HelpOutlineRounded, Share, FileCopy } from '@material-ui/icons'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import i18n from 'i18next'
import Link from 'next/link'
import Layout from '../components/layout'
import Profile from '../components/profile_components/profile'
import { Security } from '../components/profile_components/security'
import Portfolio from '../components/profile_components/portfolio'
import Technologies from '../components/profile_components/technologies'
import DefaultCoverLetter from '../components/profile_components/cover_letter'
import { MyApplications } from '../components/profile_components/my_applications'
import Background from '../components/profile_components/background'
import Generator from '../components/profile_components/generator'
import Documents from '../components/profile_components/documents'
import SharingLinks from '../components/job-offer/SharingLinks'
import { useUpdate } from '../components/reducer'
import { useAuth } from '../components/context/useFetch'
import { Trans } from 'react-i18next'
import { useAlert } from '../components/reducer'
import allPagesProps from '../components/common/all_pages_props'

const useTabPanel = makeStyles((theme) => ({
  tabPanel: {
    paddingBlock: theme.spacing(5),
    '& h1': {
      paddingBottom: theme.spacing(5),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      [theme.breakpoints.down('xs')]: {
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
      }
    }
  }
}))

function TabPanel(props) {
  const { children, value, index, ...other } = props

  const classes = useTabPanel()

  return (
    <div role="tabpanel" hidden={value !== index} id={`scrollable-force-tabpanel-${index}`} className={classes.tabPanel} aria-labelledby={`scrollable-force-tab-${index}`} {...other}>
      {value === index && (
        <>{children}</>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
}

function a11yProps(index) {
  return {
    id: `scrollable-force-tab-${index}`,
    'aria-controls': `scrollable-force-tabpanel-${index}`
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    padding: '0 !important',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'auto 1fr'
    }
  },
  tabs: {
    backgroundColor: theme.palette.background.primary,
    paddingBlock: theme.spacing(5),
    paddingInline: theme.spacing(0),
    '& .ge--MuiTabs-flexContainerVertical': {
      gap: theme.spacing(1)
    }
  },
  tab: {
    color: theme.palette.text.primary,
    textTransform: 'initial',
    fontWeight: 600,
    paddingBlock: theme.spacing(0),
    paddingInline: theme.spacing(5),
    maxWidth: '100%',
    '& .ge--MuiTab-wrapper': {
      alignItems: 'flex-start',
      textAlign: 'left'
    }
  },
  appBar: {
    width: 'inherit'
  }
}))

const Page = ({ section }) => (
  <Layout pageTitle="pages_titles.profile">
    <CandidateProfile section={section} />
  </Layout>
)

export default Page

const SECTION_MAPPING = {
  infos: 0,
  documents: 1,
  parcours: 2,
  portfolio: 3,
  technologies: 4,
  cover_letter: 5,
  candidatures: 6,
  securite: 7,
  generator: 8
}

function CandidateProfile({ section }) {
  const classes = useStyles()

  const router = useRouter()

  const [query, setQuery] = useState(SECTION_MAPPING[section || 'infos'])

  useEffect(() => {
    setQuery(SECTION_MAPPING[section || 'infos'])
  }, [section])

  const handleChange = (_event, newValue) => {
    setQuery(newValue)
  }

  const profile = useSelector((state) => state.profile)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const handleTabClick = (url) => () => {
    router.push(url, undefined, { shallow: true })
  }

  return (
    <div>
      {profile && (
        <Container maxWidth="lg">
          <Box mt={5} mb={2}>
            <Grid container justifyContent="space-between" noWrap>
              <Grid item>
                {!profile.public_porfolio_enabled ? <EnablePublicPortfolio profile={profile} /> : <EnabledPublicPortfolioActions share={profile.public_portfolio_slug} />}
              </Grid>

              <Grid item>
                <Link href="/t/me">
                  <Button id="profile-overview" variant="contained" color="primary" size="large" startIcon={<Visibility />}>
                    {isMobile ? '' : i18n.t('job_offer.new_job_offer.job.view_button')}
                  </Button>
                </Link>
              </Grid>
            </Grid>
          </Box>

          <Paper elevation={0} className={classes.root} variant="outlined">
            <Hidden smDown>
              <Tabs className={classes.tabs} value={query} indicatorColor="primary" orientation="vertical" onChange={handleChange}>
                <Tab label={i18n.t('profile.candidat.personal_info')} {...a11yProps(0)} className={classes.tab} onClick={handleTabClick('/profile/infos')} />
                <Tab label={i18n.t('profile.candidat.documents.title')} {...a11yProps(0)} className={classes.tab} onClick={handleTabClick('/profile/documents')} />
                <Tab label={i18n.t('profile.candidat.background_tab')} {...a11yProps(1)} className={classes.tab} onClick={handleTabClick('/profile/parcours')} />
                <Tab label={i18n.t('profile.candidat.portfolio_tab')} {...a11yProps(2)} className={classes.tab} onClick={handleTabClick('/profile/portfolio')} />
                <Tab label={i18n.t('profile.candidat.technologies_tab')} {...a11yProps(3)} className={classes.tab} onClick={handleTabClick('/profile/technologies')} />
                <Tab label={i18n.t('profile.candidat.cover_letter_tab')} {...a11yProps(4)} className={classes.tab} onClick={handleTabClick('/profile/cover_letter')} />
                <Tab label={i18n.t('profile.candidat.candidature_tab')} {...a11yProps(5)} className={classes.tab} onClick={handleTabClick('/profile/candidatures')} />
                <Tab label={i18n.t('profile.entreprise.security')} {...a11yProps(6)} className={classes.tab} onClick={handleTabClick('/profile/securite')} />
                <Tab label={i18n.t('profile.entreprise.generator')} {...a11yProps(7)} className={classes.tab} onClick={handleTabClick('/profile/generator')} />
              </Tabs>
            </Hidden>
            <Hidden mdUp>
              <AppBar position="static" style={{ maxWidth: '85vw' }}>
                <Tabs value={query} onChange={handleChange} variant="scrollable" style={{ overflowWrap: 'anywhere' }} aria-label="scrollable force tabs example">
                  <Tab label={i18n.t('profile.candidat.personal_info')} {...a11yProps(0)} className={classes.tab} onClick={handleTabClick('/profile/infos')} />
                  <Tab label={i18n.t('profile.candidat.documents.title')} {...a11yProps(0)} className={classes.tab} onClick={handleTabClick('/profile/documents')} />
                  <Tab label={i18n.t('profile.candidat.background_tab')} {...a11yProps(1)} className={classes.tab} onClick={handleTabClick('/profile/parcours')} />
                  <Tab label={i18n.t('profile.candidat.portfolio_tab')} {...a11yProps(2)} className={classes.tab} onClick={handleTabClick('/profile/portfolio')} />
                  <Tab label={i18n.t('profile.candidat.technologies_tab')} {...a11yProps(3)} className={classes.tab} onClick={handleTabClick('/profile/technologies')} />
                  <Tab label={i18n.t('profile.candidat.cover_letter_tab')} {...a11yProps(4)} className={classes.tab} onClick={handleTabClick('/profile/cover_letter')} />
                  <Tab label={i18n.t('profile.candidat.candidature_tab')} {...a11yProps(5)} className={classes.tab} onClick={handleTabClick('/profile/candidatures')} />
                  <Tab label={i18n.t('profile.entreprise.security')} {...a11yProps(6)} className={classes.tab} onClick={handleTabClick('/profile/securite')} />
                  <Tab label={i18n.t('profile.entreprise.generator')} {...a11yProps(7)} className={classes.tab} onClick={handleTabClick('/profile/generator')} />
                </Tabs>
              </AppBar>
            </Hidden>
            {query === 0 && (
              <TabPanel value={query} index={0} style={{ width: '100%' }}>
                <Profile />
              </TabPanel>
            )}
            {query === 1 && (
              <TabPanel value={query} index={1} style={{ width: '100%' }}>
                <Documents />
              </TabPanel>
            )}
            {query === 2 && (
              <TabPanel value={query} index={2} style={{ width: '100%' }}>
                <Background />
              </TabPanel>
            )}
            {query === 3 && (
              <TabPanel value={query} index={3} style={{ width: '100%' }}>
                <Portfolio />
              </TabPanel>
            )}
            {query === 4 && (
              <TabPanel value={query} index={4} style={{ width: '100%' }}>
                <Technologies />
              </TabPanel>
            )}
            {query === 5 && (
              <TabPanel value={query} index={5} style={{ width: '100%' }}>
                <DefaultCoverLetter />
              </TabPanel>
            )}
            {query === 6 && (
              <TabPanel value={query} index={6} style={{ width: '100%' }}>
                <MyApplications />
              </TabPanel>
            )}
            {query === 7 && (
              <TabPanel value={query} index={7} style={{ width: '100%' }}>
                <Security />
              </TabPanel>
            )}
            {query === 8 && (
              <TabPanel value={query} index={8} style={{ width: '100%' }}>
                <Generator />
              </TabPanel>
            )}
          </Paper>
        </Container>
      )}
    </div>
  )
}

export const EnabledPublicPortfolioActions = ({ slug }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const profile = useSelector((state) => state.profile)

  const publicUrl = `${window.location.origin}/${profile.user.locale === 'fr' ? '' : `${profile.user.locale}/`}t/${profile.public_porfolio_slug}`
  const auth = useAuth()
  const alert = useAlert()
  const update = useUpdate()

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(publicUrl)
    alert('notice', <Trans i18nKey="job_offer.new_job_offer.editor_dictionnary.codeCopied" />)
  }

  const deactivatePublicProfile = (event) => {
    event.preventDefault()

    const confirm = window.confirm(i18n.t('profile.candidat.public_profil.disable_profil_confirm'))
    if (confirm) {
      auth
        .fetch('profile', { method: 'PATCH', body: { profile: { public_porfolio_enabled: false } } })
        .then((profile) => {
          update(['profile', 'public_porfolio_enabled'], false)

          alert('notice', <Trans i18nKey="alerts.saved_data" />)
        })
        .catch(() => {
          alert('error', <Trans i18nKey="alerts.saved_error" />)
        })
    }
  }

  return (
    <Fragment>
      <Typography style={{ marginTop: '5px' }}>
        <TextField
          value={publicUrl}
          variant="standard"
          inputProps={{ readOnly: true, style: { fontSize: '13px', width: '190px' } }}
          onFocus={(event) => {
            event.target.select()
          }}
        />
        <IconButton size="large" aria-label="close" onClick={handleClick}>
          <Share />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          style={{ top: '50px' }}
          PaperProps={{
            style: {
              width: '300px'
            }
          }}>
          <MenuItem>
            <Box my={2}>
              <SharingLinks
                url={publicUrl}
                label={i18n.t('profile.candidat.portfolio.public_profile_share')}
                logo={{ record_type: 'Candidate', record_id: profile.id, name: 'picture', created_at: profile.picture_blob?.created_at }}
              />
            </Box>
          </MenuItem>
        </Menu>
        <IconButton size="large" aria-label="close" onClick={handleCopy}>
          <FileCopy />
        </IconButton>
      </Typography>
      <Typography style={{ fontSize: '12px' }}>
        <a onClick={deactivatePublicProfile} href="#">
          {i18n.t('profile.candidat.public_profil.disable_profil')}
        </a>
      </Typography>
    </Fragment>
  )
}

export const EnablePublicPortfolio = () => {
  const profile = useSelector((state) => state.profile)

  const auth = useAuth()
  const alert = useAlert()
  const update = useUpdate()

  const handleCheckChange = () => {
    auth
      .fetch('profile', { method: 'PATCH', body: { profile: { public_porfolio_enabled: true } } })
      .then(() => {
        auth.fetch('profile').then((modifiedProfile) => {
          update(['profile'], modifiedProfile)
          alert('notice', <Trans i18nKey="alerts.saved_data" />)
        })
      })
      .catch(() => {
        alert('error', <Trans i18nKey="alerts.saved_error" />)
      })
  }

  return (
    <Fragment>
      <Typography style={{ marginTop: '5px' }}>
        <FormControlLabel
          id="public-portfolio-activation"
          control={<Switch color="primary" />}
          label={i18n.t('profile.candidat.public_profil.enable')}
          checked={profile.public_porfolio_enabled}
          onChange={handleCheckChange}
        />
        <Tooltip title={i18n.t('profile.candidat.public_profil.tooltip_text')}>
          <HelpOutlineRounded style={{ verticalAlign: 'middle' }} />
        </Tooltip>
      </Typography>
    </Fragment>
  )
}

export const getServerSideProps = async (context) => ({ props: await allPagesProps(context) })
